import {
  Component,
  OnInit,
  Input,
  OnChanges,
  OnDestroy,
  HostBinding,
  SimpleChanges,
} from '@angular/core';
import { sub } from 'date-fns';
import { Platform } from '@ionic/angular';
import { observable, computed } from 'mobx-angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, from, firstValueFrom } from 'rxjs';
import { formatForApi } from 'netsocial-lib/src/app/utils/date-utils';
import { propertyDidChange } from 'netsocial-lib/src/app/utils/component-utils';
import { PaginatedItems } from 'netsocial-lib/src/app/interfaces/paginated-items';
import { ApiRequestService } from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { handleError } from 'netsocial-posting/src/app/services/error/error.service';
import {
  LeaderboardService,
  GetLeaderboardParams,
} from '../../../services/leaderboard/leaderboard.service';
import { TableColumn } from '../../../interfaces/table-column';
import { LeaderboardEntry } from '../../../interfaces/leaderboard-entry';
import { NsTableComponent } from '../../table/ns-table/ns-table.component';

interface LeaderboardHeaderItem {
  label: string;
  value: string | LeaderboardEntry;
  key: string;
  loading: boolean;
}

@Component({
  selector: 'leaderboard-viewer',
  templateUrl: './leaderboard-viewer.component.html',
  styleUrls: ['./leaderboard-viewer.component.scss'],
})
export class LeaderboardViewerComponent
  extends NsTableComponent<LeaderboardEntry>
  implements OnInit, OnChanges, OnDestroy
{
  @observable
  paginatedLeaderboardEntries?: PaginatedItems<LeaderboardEntry>;
  @Input() period: GetLeaderboardParams['period'] = 90;

  @HostBinding('class.full-view')
  @Input()
  fullView: boolean;

  @observable @Input() startDate: Date;
  @observable @Input() endDate: Date;
  @Input() hidePaging: boolean;

  @Input() teamMemberName: GetLeaderboardParams['teamMemberName'];
  @Input() teamIds?: GetLeaderboardParams['teamIds'];

  @observable @Input() perPage: number;

  @observable columns: TableColumn[] = [
    {
      key: 'order',
      title: 'rankLabel',
      sortable: false,
    },
    {
      key: 'profile',
      title: 'teamMemberLabel',
      sortable: false,
    },
    {
      key: 'badges',
      title: 'badgesLabel',
    },
    {
      key: 'deliveries',
      title: 'deliveriesLabel',
      sortable: false,
    },
    {
      key: 'clicks',
      title: 'clicksLabel',
      sortable: false,
    },
    {
      key: 'shares',
      title: 'sharesLabel',
      sortable: false,
    },
    {
      key: 'impressions',
      title: 'impressionsLabel',
      sortable: false,
    },
    {
      key: 'likes',
      title: 'likesLabel',
      sortable: false,
    },
    {
      key: 'value',
      title: 'earnedValueLabel',
      sortable: false,
    },
  ];

  @observable earnedValueLeaderEntry?: LeaderboardEntry;
  @observable mostClicksLeaderEntry?: LeaderboardEntry;
  @observable mostImpressionsLeaderEntry?: LeaderboardEntry;
  @observable deliveriesLeaderboardEntry?: LeaderboardEntry;

  @observable getLeaderboardEntriesSubscription?: Subscription;
  @observable getLeaderboardEntriesPageSubscription?: Subscription;

  @computed
  get loading(): boolean {
    return !!this.getLeaderboardEntriesSubscription;
  }

  @computed
  get headerItems(): LeaderboardHeaderItem[] {
    return [
      {
        label: 'components.leaderboardViewer.titles.greatestValue',
        value: this.earnedValueLeaderEntry ?? '',
        key: 'value',
        loading: this.loading,
      },
      {
        label: 'components.leaderboardViewer.titles.mostClicks',
        value: this.mostClicksLeaderEntry ?? '',
        key: 'clicks',
        loading: this.loading,
      },
      {
        label: 'components.leaderboardViewer.titles.mostImpressions',
        value: this.mostImpressionsLeaderEntry ?? '',
        key: 'impressions',
        loading: this.loading,
      },
      {
        label: 'components.leaderboardViewer.titles.mostDeliveries',
        key: 'deliveries',
        value: this.deliveriesLeaderboardEntry ?? '',
        loading: this.loading,
      },
    ];
  }

  isDesktop: boolean;

  private contextOwnerSubscription: Subscription;

  constructor(
    private readonly translate: TranslateService,
    private readonly apiRequest: ApiRequestService,
    private readonly leaderboardService: LeaderboardService,
    private readonly platform: Platform
  ) {
    super();
  }

  ngOnInit(): void {
    this.configureSortMeta();
    this.setColumns();

    this.contextOwnerSubscription = this.apiRequest.getContextOwner().subscribe(() => {
      this.getLeaderboardEntries();
    });

    this.isDesktop = this.platform.is('desktop');
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (propertyDidChange(changes).length) {
      this.getLeaderboardEntries();
    }
  }

  ngOnDestroy(): void {
    this.contextOwnerSubscription.unsubscribe();
    this.getLeaderboardEntriesSubscription?.unsubscribe();
  }

  async getLeaderboardEntries(first?: number): Promise<void> {
    const { startDate, endDate } = this;

    const params: GetLeaderboardParams = {
      perPage: this.perPage,
    };

    if (this.teamMemberName?.trim()) {
      params.teamMemberName = this.teamMemberName.trim();
    }

    if (first !== undefined) {
      params.first = first;
    }

    if (this.period && !this.startDate) {
      this.endDate = new Date();
      this.startDate = sub(this.endDate, { days: this.period });
    }

    if (startDate && endDate) {
      params.startDate = formatForApi(startDate);
      params.endDate = formatForApi(endDate);
    }

    if (this.teamIds?.length) {
      params.teamIds = this.teamIds;
    }

    this.getLeaderboardEntriesSubscription?.unsubscribe();

    this[
      first === undefined
        ? 'getLeaderboardEntriesSubscription'
        : 'getLeaderboardEntriesPageSubscription'
    ] = from(this.leaderboardService.getLeaderboard(params)).subscribe(
      (response) => {
        if (first === undefined) {
          this.paginatedLeaderboardEntries = response.page;
        } else {
          this.paginatedLeaderboardEntries?.loadNextPage(response.page);
        }

        this.earnedValueLeaderEntry = response.topPerformers.greatestEarnedValue;
        this.mostClicksLeaderEntry = response.topPerformers.mostClicks;
        this.mostImpressionsLeaderEntry = response.topPerformers.mostImpressions;
        this.deliveriesLeaderboardEntry = response.topPerformers.mostDeliveredPosts;

        this._data.next(this.paginatedLeaderboardEntries!.items);
        this.dataTrack();

        this.getLeaderboardEntriesSubscription = undefined;
        this.getLeaderboardEntriesPageSubscription = undefined;
      },
      (error) => {
        handleError(error);
      }
    );
  }

  setColumns(): void {
    this.columns.forEach((column) => {
      firstValueFrom(this.translate.get(`components.leaderboard.labels.${column.title}`)).then(
        (title) => {
          column.title = title;
        }
      );
    });
  }
}
